import { shortPieceNames } from 'config/legacy-themes';
import type { ThemeAssets } from 'config/theme-assets';
import { validateThemeAssets2D } from 'config/theme-validation-utils';
import { getLegacyPieceBaseURL } from 'model/board/get-legacy-piece-base-url';
import { Board } from 'model/board/types';
import { testImageUrl } from 'plugins/assets/helpers/test-image-url';
import fallbackPieces from 'renderers/default/assets/default-pieces-base64.json';

/**
 * Test if piece assets will load
 */
export function verifyPiecesAssetsExistence(
  options: Board.Options,
  pieceStyleObj?: { format: string; path: string; isPseudo3d?: boolean },
) {
  return options.themeAssets
    ? piecesFallbackTestThemeAssets(options.themeAssets)
    : piecesFallbackTestLegacy(pieceStyleObj, options);
}

/**
 * Test if piece assets will load using themeAssets object
 * @param themeAssets
 */
function piecesFallbackTestThemeAssets(themeAssets: ThemeAssets.Assets) {
  return new Promise<boolean>((resolve, reject) => {
    try {
      validateThemeAssets2D(themeAssets);
    } catch {
      // don't validate assets for invalid or 3D assets
      return resolve(true);
    }

    const urls: string[] = [];
    Object.keys(themeAssets.pieces.assets).forEach((key) => {
      if (themeAssets.pieces.assets[key] !== fallbackPieces[key]) {
        urls.push(themeAssets.pieces.assets[key]);
      }
    });

    if (urls.length === 0) {
      return resolve(true);
    }

    const urlPromises = urls.map((url) => testImageUrl(url));

    // could run into rate limiting issues, causing us to always fallback
    Promise.all(urlPromises)
      .then(() => resolve(true))
      .catch((err) => {
        reject(new Board.StylesSetFailed(err.message));
      });
  });
}

/**
 * Test if piece assets will load using legacy pieceStyles
 */
function piecesFallbackTestLegacy(
  pieceStyleObj?: { format: string; path: string; isPseudo3d?: boolean },
  options?: Board.Options,
) {
  return new Promise<boolean>(function (resolve, reject) {
    if (!pieceStyleObj) {
      reject(new Board.StylesSetFailed('Piece style not found'));
      return;
    }

    const pieceBaseUrl = getLegacyPieceBaseURL(pieceStyleObj.path, undefined, options);
    const everyPieceURL = shortPieceNames.map(
      (shortString) => `${pieceBaseUrl}/${shortString}.${pieceStyleObj.format}`,
    );
    const urlPromises = everyPieceURL.map((url) => testImageUrl(url));

    // could run into rate limiting issues, causing us to always fallback
    Promise.all(urlPromises)
      .then(() => resolve(true))
      .catch((err) => {
        reject(new Board.StylesSetFailed(err.message));
      });
  });
}
